import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Story } from '@sorare/core/src/components/rivals/Story';

import useMarkStoriesAsRead from '@sorare/gameplay/src/hooks/stories/useMarkStoriesAsRead';

import { RankChange } from 'components/stories/rivals/leaderboards/RankChange';
import { RepChange } from 'components/stories/rivals/leaderboards/RepChange';
import { usePausableStory } from 'hooks/usePausableStory';

import { ArenaStoryElement } from './ArenaStoryElement';
import {
  ArenaStory_footballRivalsAggregatedArenaStory,
  ArenaStory_footballRivalsCurrentManager,
} from './__generated__/index.graphql';

const StoryElement = styled.div`
  width: 100%;
  height: 100%;
`;

type Props = {
  story: ArenaStory_footballRivalsAggregatedArenaStory;
  manager: ArenaStory_footballRivalsCurrentManager;
};
export const ArenaStory = ({ story, manager }: Props) => {
  const { pause, handleOnPause, handleOnUnpause } = usePausableStory();
  const { readStories } = useMarkStoriesAsRead();

  const onClose = () => {
    readStories({ storyIds: [story.id] });
  };

  const challengeStories = story.challenges.map(challenge => ({
    duration: 3.5,
    bodyElement: <ArenaStoryElement key={challenge.id} challenge={challenge} />,
  }));

  const stories = [
    ...challengeStories,
    {
      duration: 4,
      bodyElement: (
        <StoryElement>
          <RepChange story={story} manager={manager} />
        </StoryElement>
      ),
    },
    {
      duration: 4,
      bodyElement: (
        <StoryElement>
          <RankChange story={story} manager={manager} />
        </StoryElement>
      ),
    },
  ];

  return (
    <Story
      onClose={onClose}
      stories={stories}
      pause={pause}
      handleOnPause={handleOnPause}
      handleOnUnpause={handleOnUnpause}
      goBackToBeginning={false}
    />
  );
};

ArenaStory.fragments = {
  footballRivalsAggregatedArenaStory: gql`
    fragment ArenaStory_footballRivalsAggregatedArenaStory on FootballRivalsAggregatedArenaStory {
      id
      challenges {
        id
        ...ArenaStoryElement_footballRivalsChallenge
      }
      ...RepChange_FootballRivalsAggregatedArenaStory
      ...RankChange_footballRivalsAggregatedArenaStory
    }
    ${ArenaStoryElement.fragments.footballRivalsChallenge}
    ${RepChange.fragments.footballRivalsAggregatedArenaStory}
    ${RankChange.fragments.footballRivalsAggregatedArenaStory}
  ` as TypedDocumentNode<ArenaStory_footballRivalsAggregatedArenaStory>,
  footballRivalsCurrentManager: gql`
    fragment ArenaStory_footballRivalsCurrentManager on FootballRivalsCurrentManager {
      id
      ...RepChange_footballRivalsCurrentManager
      ...RankChange_footballRivalsCurrentManager
    }
    ${RankChange.fragments.footballRivalsCurrentManager}
    ${RepChange.fragments.footballRivalsCurrentManager}
  ` as TypedDocumentNode<ArenaStory_footballRivalsCurrentManager>,
};
